"use client";

import { c as _c } from "react/compiler-runtime";
import UserCICAndLevel, { UserCICAndLevelSize } from "@/components/user/utils/UserCICAndLevel";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { getTimeAgoShort } from "@/helpers/Helpers";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useState } from "react";
import type { DropInteractionParams } from "../Drop";
import { DropLocation } from "../Drop";
import WaveDropActions from "../WaveDropActions";
import WaveDropAuthorPfp from "../WaveDropAuthorPfp";
import WaveDropContent from "../WaveDropContent";
import WaveDropMetadata from "../WaveDropMetadata";
import WaveDropMobileMenu from "../WaveDropMobileMenu";
import WaveDropReactions from "../WaveDropReactions";
interface EndedParticipationDropProps {
  readonly drop: ExtendedDrop;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onQuote: (param: DropInteractionParams) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
}
export default function EndedParticipationDrop(t0) {
  const $ = _c(85);
  const {
    drop,
    showWaveInfo,
    activeDrop,
    showReplyAndQuote,
    location,
    onReply,
    onQuote,
    onQuoteClick,
    onDropContentClick
  } = t0;
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const router = useRouter();
  const [activePartIndex, setActivePartIndex] = useState(0);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const [isSlideUp, setIsSlideUp] = useState(false);
  const isMobile = useIsMobileDevice();
  const hasTouch = useIsTouchDevice() || isMobile;
  let t1;
  if ($[0] !== router) {
    t1 = (e, path) => {
      e.preventDefault();
      e.stopPropagation();
      router.push(path);
    };
    $[0] = router;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleNavigation = t1;
  let t2;
  if ($[2] !== hasTouch) {
    t2 = () => {
      if (!hasTouch) {
        return;
      }
      setLongPressTriggered(true);
      setIsSlideUp(true);
    };
    $[2] = hasTouch;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleLongPress = t2;
  let t3;
  if ($[4] !== activePartIndex || $[5] !== drop || $[6] !== onReply) {
    t3 = () => {
      setIsSlideUp(false);
      onReply({
        drop,
        partId: drop.parts[activePartIndex]?.part_id
      });
    };
    $[4] = activePartIndex;
    $[5] = drop;
    $[6] = onReply;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleOnReply = t3;
  let t4;
  if ($[8] !== activePartIndex || $[9] !== drop || $[10] !== onQuote) {
    t4 = () => {
      setIsSlideUp(false);
      onQuote({
        drop,
        partId: drop.parts[activePartIndex]?.part_id
      });
    };
    $[8] = activePartIndex;
    $[9] = drop;
    $[10] = onQuote;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const handleOnQuote = t4;
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setIsSlideUp(false);
    };
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const handleOnAddReaction = t5;
  let t6;
  if ($[13] !== location) {
    t6 = () => {
      if (location === DropLocation.WAVE) {
        return "tw-bg-iron-900/60 tw-ring-1 tw-ring-inset tw-ring-iron-800";
      }
      return "tw-bg-iron-950 tw-ring-1 tw-ring-inset tw-ring-iron-800";
    };
    $[13] = location;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const getDropLocationBackground = t6;
  const dropBackgroundClass = isActiveDrop ? "tw-bg-[#3CCB7F]/10" : getDropLocationBackground();
  const t7 = `${location === DropLocation.WAVE ? "tw-px-4 tw-py-1" : ""} tw-w-full`;
  const t8 = `tw-group tw-relative tw-flex tw-w-full tw-flex-col tw-overflow-hidden tw-rounded-lg tw-px-4 tw-py-3 tw-transition-colors tw-duration-200 tw-ease-linear ${dropBackgroundClass}`;
  let t9;
  if ($[15] !== activePartIndex || $[16] !== drop || $[17] !== handleOnQuote || $[18] !== handleOnReply || $[19] !== isMobile || $[20] !== showReplyAndQuote) {
    t9 = !isMobile && showReplyAndQuote && <WaveDropActions drop={drop} activePartIndex={activePartIndex} showVoting={false} onReply={handleOnReply} onQuote={handleOnQuote} />;
    $[15] = activePartIndex;
    $[16] = drop;
    $[17] = handleOnQuote;
    $[18] = handleOnReply;
    $[19] = isMobile;
    $[20] = showReplyAndQuote;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== drop) {
    t10 = <WaveDropAuthorPfp drop={drop} />;
    $[22] = drop;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== drop.author.level) {
    t11 = <UserCICAndLevel level={drop.author.level} size={UserCICAndLevelSize.SMALL} />;
    $[24] = drop.author.level;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== drop.author.handle || $[27] !== drop.author.primary_address || $[28] !== handleNavigation) {
    t12 = e_0 => handleNavigation(e_0, `/${drop.author.handle ?? drop.author.primary_address}`);
    $[26] = drop.author.handle;
    $[27] = drop.author.primary_address;
    $[28] = handleNavigation;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const t13 = `/${drop.author.handle ?? drop.author.primary_address}`;
  const t14 = drop.author.handle ?? drop.author.primary_address;
  let t15;
  if ($[30] !== t12 || $[31] !== t13 || $[32] !== t14) {
    t15 = <p className="tw-mb-0 tw-text-md tw-font-semibold tw-leading-none"><Link onClick={t12} href={t13} className="tw-text-iron-200 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-500">{t14}</Link></p>;
    $[30] = t12;
    $[31] = t13;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="tw-size-[3px] tw-flex-shrink-0 tw-rounded-full tw-bg-iron-600" />;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== drop.created_at) {
    t17 = getTimeAgoShort(drop.created_at);
    $[35] = drop.created_at;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t17) {
    t18 = <p className="tw-mb-0 tw-whitespace-nowrap tw-text-xs tw-font-normal tw-leading-none tw-text-iron-500">{t17}</p>;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== t11 || $[40] !== t15 || $[41] !== t18) {
    t19 = <div className="tw-flex tw-items-center tw-gap-x-2">{t11}{t15}{t16}{t18}</div>;
    $[39] = t11;
    $[40] = t15;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <div className="tw-flex tw-w-fit tw-items-center tw-whitespace-nowrap tw-rounded-md tw-border tw-border-solid tw-border-iron-500/25 tw-bg-iron-600/10 tw-px-2 tw-py-0.5 tw-font-medium tw-text-iron-500"><span className="tw-text-xs">Participant</span></div>;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t19) {
    t21 = <div className="tw-flex tw-flex-col tw-gap-y-2">{t19}{t20}</div>;
    $[44] = t19;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== drop.wave || $[47] !== handleNavigation || $[48] !== showWaveInfo) {
    t22 = showWaveInfo && (() => {
      const waveMeta = (drop.wave as unknown as {
        chat?: {
          scope?: {
            group?: {
              is_direct_message?: boolean | undefined;
            } | undefined;
          } | undefined;
        } | undefined;
      }).chat;
      const isDirectMessage = waveMeta?.scope?.group?.is_direct_message ?? false;
      const waveHref = getWaveRoute({
        waveId: drop.wave.id,
        isDirectMessage,
        isApp: false
      });
      return <Link href={waveHref} onClick={e_1 => handleNavigation(e_1, waveHref)} className="tw-leading-0 tw-text-[11px] tw-text-iron-500 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-300">{drop.wave.name}</Link>;
    })();
    $[46] = drop.wave;
    $[47] = handleNavigation;
    $[48] = showWaveInfo;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== activePartIndex || $[51] !== drop || $[52] !== handleLongPress || $[53] !== hasTouch || $[54] !== onDropContentClick || $[55] !== onQuoteClick) {
    t23 = <WaveDropContent drop={drop} activePartIndex={activePartIndex} setActivePartIndex={setActivePartIndex} onLongPress={handleLongPress} onDropContentClick={onDropContentClick} onQuoteClick={onQuoteClick} setLongPressTriggered={setLongPressTriggered} isCompetitionDrop={true} hasTouch={hasTouch} />;
    $[50] = activePartIndex;
    $[51] = drop;
    $[52] = handleLongPress;
    $[53] = hasTouch;
    $[54] = onDropContentClick;
    $[55] = onQuoteClick;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  let t24;
  if ($[57] !== t21 || $[58] !== t22 || $[59] !== t23) {
    t24 = <div className="tw-flex tw-w-full tw-flex-col tw-gap-y-2">{t21}{t22}{t23}</div>;
    $[57] = t21;
    $[58] = t22;
    $[59] = t23;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  let t25;
  if ($[61] !== t10 || $[62] !== t24) {
    t25 = <div className="tw-flex tw-w-full tw-gap-x-3 tw-border-0 tw-bg-transparent tw-text-left">{t10}{t24}</div>;
    $[61] = t10;
    $[62] = t24;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  let t26;
  if ($[64] !== drop.metadata) {
    t26 = drop.metadata.length > 0 && <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1"><WaveDropMetadata metadata={drop.metadata} /></div>;
    $[64] = drop.metadata;
    $[65] = t26;
  } else {
    t26 = $[65];
  }
  let t27;
  if ($[66] !== drop) {
    t27 = <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1"><WaveDropReactions drop={drop} /></div>;
    $[66] = drop;
    $[67] = t27;
  } else {
    t27 = $[67];
  }
  let t28;
  if ($[68] !== drop || $[69] !== handleOnQuote || $[70] !== handleOnReply || $[71] !== isSlideUp || $[72] !== longPressTriggered || $[73] !== showReplyAndQuote) {
    t28 = <WaveDropMobileMenu drop={drop} isOpen={isSlideUp} longPressTriggered={longPressTriggered} showReplyAndQuote={showReplyAndQuote} setOpen={setIsSlideUp} onReply={handleOnReply} onQuote={handleOnQuote} onAddReaction={handleOnAddReaction} />;
    $[68] = drop;
    $[69] = handleOnQuote;
    $[70] = handleOnReply;
    $[71] = isSlideUp;
    $[72] = longPressTriggered;
    $[73] = showReplyAndQuote;
    $[74] = t28;
  } else {
    t28 = $[74];
  }
  let t29;
  if ($[75] !== t25 || $[76] !== t26 || $[77] !== t27 || $[78] !== t28 || $[79] !== t8 || $[80] !== t9) {
    t29 = <div className={t8}>{t9}{t25}{t26}{t27}{t28}</div>;
    $[75] = t25;
    $[76] = t26;
    $[77] = t27;
    $[78] = t28;
    $[79] = t8;
    $[80] = t9;
    $[81] = t29;
  } else {
    t29 = $[81];
  }
  let t30;
  if ($[82] !== t29 || $[83] !== t7) {
    t30 = <div className={t7}>{t29}</div>;
    $[82] = t29;
    $[83] = t7;
    $[84] = t30;
  } else {
    t30 = $[84];
  }
  return t30;
}